import Dialog from './Dialog';
import common from '../common';
import Text from './Text';
import i18next from 'i18next';

export default class CreditsPanel extends Dialog {
	constructor(w, h, cb) {
		super(w, h, cb);

		this._width = w;
		this._entries = [];

		this._scrollUpHeld = false;
		this._scrollDownHeld = false;

		this.init();
	}

	init() {
		this.setTitleText('gui.splashscreen.buttons.credits');

		this.scrollView = new P3.utils.ScrollView();
		this.scrollView.contentBounds = new PIXI.Rectangle();
		this.scrollView.hitArea = new PIXI.Rectangle(0, 20, this._width, this._height);
		this.scrollView.position.set(this._width * -0.5, this._height * -0.5 + 110);
		this.addChild(this.scrollView);

		this.scrollView.content.mask = new PIXI.Graphics();
		this.scrollView.content.mask.beginFill(0xFF0000);
		this.scrollView.content.mask.drawRect(0, -20, this.body.width, this._height - 120);
		this.scrollView.addChild(this.scrollView.content.mask);

		/** SCROLL BAR **/
		this.scrollBar = this._createScrollBar(this._height - 220);
		this.scrollBar.position.x = this.width * 0.5 - 60;
		this.scrollBar.position.y = -(this.scrollBar.height * 0.5) + 60;
		this.addChild(this.scrollBar);

		this._populateDialog(this._width);
	}

	_populateDialog(width) {
		const entry = new PIXI.Container();

		entry.body = new Text(i18next.t('gui.splashscreen.creditsdialog.body'), { font: '28px', align: 'left', tint: 0x0, maxWidth: width - 150 });
		entry.body.anchor.set(0);
		entry.body.position.set(50, 0);
		entry.addChild(entry.body);

		entry.hyperlink = new Text('getinvolved', { font: '28px', align: 'left', tint: 0x0, maxWidth: width - 150 });

		entry.hyperlink.interactive = true;
		entry.hyperlink.buttonMode = true;
		entry.hyperlink.on('pointerup', this._onHyperlinkClick);
		entry.addChild(entry.hyperlink);

		entry.hyperlink.underline = new PIXI.Sprite(PIXI.Texture.WHITE);
		entry.hyperlink.underline.tint = 0x0;
		entry.hyperlink.underline.width = entry.hyperlink.width;
		entry.hyperlink.underline.height = 2;
		entry.hyperlink.underline.anchor.set(0, 1);
		entry.hyperlink.underline.position.set(0, entry.hyperlink.height);
		entry.hyperlink.addChild(entry.hyperlink.underline);

		entry.hyperlink.position.set(50, entry.body.height + 20);

		this.scrollView.content.addChild(entry);
		this._entries.push(entry);

		const bounds = this.scrollView.contentBounds;

		bounds.y = -this._entries.length * (entry.height - this._height) + bounds.height;
		if (bounds.height - bounds.y > -200) {
			bounds.y += -200;
		} else {
			bounds.y = bounds.height;
			this.scrollBar.visible = false;
		}
	}

	_createScrollBar(height) {
		const scroll = new PIXI.Container();

		scroll.upButton = common.buttonFactory.createButton({
			normal: P3.assets.texture('btn_scroll_chevron_out'),
		});

		scroll.upButton.signals.down.add(this.onScrollbarUpButtonPointerDown, this);
		scroll.upButton.signals.click.add(this.onScrollbarUpButtonPointerUp, this);
		scroll.upButton.position.set(7, -20);

		scroll.upButton.accessibleTitle = i18next.t('accessibility.button.scrollUp');

		scroll.thumb = new PIXI.NineSlicePlane(P3.assets.texture('scroll_fg_9slice'), 12, 12, 12, 12);
		scroll.thumb.height = 50;
		scroll.thumb.width = 15;
		scroll.thumb.interactive = true;
		scroll.thumb.buttonMode = true;
		scroll.thumb.on('mousedown', this.onScrollBarThumbPointerDown, this);
		scroll.thumb.on('mouseup', this.onScrollBarThumbPointerUp, this);
		scroll.thumb.on('mouseupoutside', this.onScrollBarThumbPointerUp, this);
		scroll.thumb.on('touchstart', this.onScrollBarThumbPointerDown, this);
		scroll.thumb.on('touchend', this.onScrollBarThumbPointerUp, this);
		scroll.thumb.on('touchendoutside', this.onScrollBarThumbPointerUp, this);

		scroll.track = new PIXI.NineSlicePlane(P3.assets.texture('scroll_bg_9slice'), 12, 12, 12, 12);
		scroll.track.height = height;
		scroll.track.width = scroll.thumb.width;

		scroll.downButton = common.buttonFactory.createButton({
			normal: P3.assets.texture('btn_scroll_chevron_out'),
		});
		scroll.downButton.signals.down.add(this.onScrollbarDownButtonPointerDown, this);
		scroll.downButton.signals.up.add(this.onScrollbarDownButtonPointerUp, this);
		scroll.downButton.scale.y *= -1;
		scroll.downButton.position.set(7, height + 20);

		scroll.downButton.accessibleTitle = i18next.t('accessibility.button.scrollDown');

		scroll.addChild(scroll.upButton);
		scroll.addChild(scroll.track);
		scroll.addChild(scroll.thumb);
		scroll.addChild(scroll.downButton);

		return scroll;
	}

	onScrollBarThumbPointerDown(event) {
		this._isScrolling = true;
		this._scrollOffset = event.data.getLocalPosition(this.scrollBar.thumb);
	}

	onScrollBarThumbPointerUp() {
		this._isScrolling = false;
	}

	onScrollbarUpButtonPointerDown() {
		this._scrollUpHeld = true;
	}

	onScrollbarUpButtonPointerUp() {
		this._scrollUpHeld = false;
	}

	onScrollbarDownButtonPointerDown() {
		this._scrollDownHeld = true;
	}

	onScrollbarDownButtonPointerUp() {
		this._scrollDownHeld = false;
	}

	updateScrollThumb() {
		const thumb = this.scrollBar.thumb;
		const thumbRange = this.scrollBar.track.height - thumb.height + 5;

		if (this._isScrolling) {
			const position = this.scrollBar.toLocal(P3.input.touch, common.stage);

			thumb.y = Math.min(thumbRange, Math.max(0, position.y - this._scrollOffset.y));
			this.scrollView.content.y = this.scrollView.contentBounds.height + ((thumb.y / thumbRange)
                * (this.scrollView.contentBounds.y - this.scrollView.contentBounds.height));
		} else {
			const contentRange = this.scrollView.contentBounds.y - this.scrollView.contentBounds.height;
			const f = (this.scrollView.content.y - this.scrollView.contentBounds.height) / contentRange;

			thumb.y = Math.min(thumbRange, Math.max(0, f * thumbRange));
		}
	}

	update() {
		if (this._scrollUpHeld) {
			this.scrollView.content.y += 5;
		} else if (this._scrollDownHeld) {
			this.scrollView.content.y -= 5;
		}

		this.scrollView.update(window.dt);
		this.updateScrollThumb();
	}

	_onHyperlinkClick() {
		window.open('https://www.preventionweb.net/stop-disasters-game/get-involved', '_blank');
	}
}
